/**
 * The preview pane: what the report will be, while you are changing it.
 *
 * Two modes, deliberately:
 *
 *   Live    an HTML rendering of the document — cover, contents, the scope it
 *           will draw on, the sections it will print — redrawn on every
 *           keystroke. Instant, and honest about being a rendering of the
 *           configuration rather than of the typeset page.
 *
 *   Exact   the real PDF from the same builders that produce the delivered
 *           file, in an iframe. Slower, so it is asked for rather than run on
 *           every change; auto-refreshed (debounced) once you have asked, so
 *           the exact view keeps up while you keep editing.
 *
 * Downloads never come from the live view. The file a person sends is always
 * the server's bytes.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Eye, FileText, Loader2, RefreshCw } from 'lucide-react'
import type { Loan } from '@/types'
import { reportsService } from '@/services/loans'
import type { ReportBranding } from '@/services/reportBranding'
import {
  missingRequirement,
  sectionsFor,
  subjectFor,
  type ReportTypeSpec,
} from './reportTypes'

type Props = {
  spec: ReportTypeSpec
  name: string
  filters: Record<string, unknown>
  /** What the engine is actually given, when it differs from the controls'
   *  filters: a saved pipeline view renders with its columns (`viewGrid`). */
  engine?: { template: string; filters: Record<string, unknown> }
  deals: Loan[]
  branding?: ReportBranding
  /** Rows the report will draw on, for the live scope table. */
  scopeRows: Array<{ id: string; primary: string; secondary: string; trailing: string }>
  scopeLabel: string
  scopeEmptyMessage: string
}

const DEBOUNCE_MS = 700

export function ReportPreviewPane({
  spec,
  name,
  filters,
  engine,
  deals,
  branding,
  scopeRows,
  scopeLabel,
  scopeEmptyMessage,
}: Props) {
  const engineTemplate = engine?.template ?? spec.engineTemplate
  const engineFilters = engine?.filters ?? filters
  const [mode, setMode] = useState<'live' | 'exact'>('live')
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [rendering, setRendering] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestSeq = useRef(0)
  const objectUrl = useRef<string | null>(null)

  const blocked = missingRequirement(spec, filters)
  const fingerprint = useMemo(
    () => JSON.stringify({ template: engineTemplate, filters: engineFilters, brand: branding?.pdf }),
    [branding?.pdf, engineFilters, engineTemplate],
  )

  const renderExact = useCallback(async () => {
    if (blocked) return
    const seq = ++requestSeq.current
    setRendering(true)
    setError(null)
    try {
      const response = await reportsService.preview({
        template: engineTemplate,
        filters: engineFilters,
      })
      // A slower earlier request must never overwrite a newer one's result.
      if (seq !== requestSeq.current) return
      const blob = response.data as Blob
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error('The report engine returned an empty document.')
      }
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current)
      objectUrl.current = URL.createObjectURL(blob)
      setPdfUrl(objectUrl.current)
    } catch (cause) {
      if (seq !== requestSeq.current) return
      setError(await previewErrorMessage(cause))
      setPdfUrl(null)
    } finally {
      if (seq === requestSeq.current) setRendering(false)
    }
  }, [blocked, engineFilters, engineTemplate])

  // Once the exact view is open, keep it current as editing continues — but
  // debounced, because each refresh is a full typesetting pass on the server.
  useEffect(() => {
    if (mode !== 'exact' || blocked) return
    const timer = window.setTimeout(() => void renderExact(), DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [blocked, fingerprint, mode, renderExact])

  useEffect(
    () => () => {
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current)
    },
    [],
  )

  const pdf = branding?.pdf

  return (
    <aside className="flex min-h-0 flex-1 flex-col rounded-lg border border-amber-400/15 bg-[#0d0d0d]">
      <div className="flex shrink-0 items-center gap-2 border-b border-amber-400/12 px-3 py-2">
        <span className="text-[9px] font-semibold uppercase tracking-[0.2em] text-amber-300/85">
          Preview
        </span>
        <div className="ml-auto flex items-center gap-1 rounded border border-amber-400/15 p-0.5">
          <button
            type="button"
            onClick={() => setMode('live')}
            aria-pressed={mode === 'live'}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] ${
              mode === 'live' ? 'bg-amber-400/90 text-black' : 'text-stone-400 hover:text-amber-200'
            }`}
          >
            <Eye className="h-3 w-3" /> Live
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('exact')
              void renderExact()
            }}
            aria-pressed={mode === 'exact'}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] ${
              mode === 'exact' ? 'bg-amber-400/90 text-black' : 'text-stone-400 hover:text-amber-200'
            }`}
          >
            <FileText className="h-3 w-3" /> Exact PDF
          </button>
        </div>
        {mode === 'exact' && (
          <button
            type="button"
            onClick={() => void renderExact()}
            disabled={rendering || Boolean(blocked)}
            aria-label="Re-render the exact PDF"
            className="rounded border border-amber-400/15 p-1 text-stone-400 hover:text-amber-200 disabled:opacity-40"
          >
            {rendering ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
          </button>
        )}
      </div>

      {blocked && (
        <div
          role="status"
          className="flex shrink-0 items-start gap-2 border-b border-amber-400/12 bg-amber-400/[0.05] px-3 py-2 text-[10px] leading-relaxed text-amber-200"
        >
          <AlertTriangle className="mt-px h-3 w-3 shrink-0" />
          {blocked}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        {mode === 'exact' ? (
          <ExactView error={error} pdfUrl={pdfUrl} rendering={rendering} blocked={blocked} />
        ) : (
          <LiveDocument
            spec={spec}
            name={name}
            filters={filters}
            deals={deals}
            pdf={pdf}
            scopeRows={scopeRows}
            scopeLabel={scopeLabel}
            scopeEmptyMessage={scopeEmptyMessage}
          />
        )}
      </div>

      <p className="shrink-0 border-t border-amber-400/10 px-3 py-2 text-[9px] leading-relaxed text-stone-500">
        {mode === 'exact'
          ? 'This is the document the engine produces, rendered from live data in your brand guidelines. Downloads use these exact bytes.'
          : 'A live rendering of the configuration — the cover, the sections and the records in scope. Switch to Exact PDF for the typeset document.'}
      </p>
    </aside>
  )
}

function ExactView({
  blocked,
  error,
  pdfUrl,
  rendering,
}: {
  blocked: string | null
  error: string | null
  pdfUrl: string | null
  rendering: boolean
}) {
  if (blocked) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center p-6 text-center text-[11px] text-stone-500">
        Supply what the report needs and the exact document will render here.
      </div>
    )
  }
  if (error) {
    return (
      <div
        role="alert"
        className="m-3 rounded border border-red-400/30 bg-red-500/10 px-3 py-2 text-[11px] leading-relaxed text-red-200"
      >
        {error}
      </div>
    )
  }
  if (!pdfUrl) {
    return (
      <div className="flex h-full min-h-[320px] items-center justify-center gap-2 p-6 text-[11px] text-stone-500">
        {rendering ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Typesetting the document…
          </>
        ) : (
          'Nothing rendered yet.'
        )}
      </div>
    )
  }
  return (
    <div className="relative h-full min-h-[420px]">
      {rendering && (
        <div className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded border border-amber-400/20 bg-black/80 px-2 py-1 text-[9px] text-amber-200">
          <Loader2 className="h-3 w-3 animate-spin" /> Updating
        </div>
      )}
      <iframe title="Exact report preview" src={pdfUrl} className="h-full w-full border-0" />
    </div>
  )
}

/**
 * The live rendering. Painted in the tenant's own PDF palette so a brand change
 * is visible here immediately, without waiting for a server round trip.
 */
function LiveDocument({
  spec,
  name,
  filters,
  deals,
  pdf,
  scopeRows,
  scopeLabel,
  scopeEmptyMessage,
}: {
  spec: ReportTypeSpec
  name: string
  filters: Record<string, unknown>
  deals: Loan[]
  pdf?: ReportBranding['pdf']
  scopeRows: Props['scopeRows']
  scopeLabel: string
  scopeEmptyMessage: string
}) {
  const accent = pdf?.accent ?? '#0293D3'
  const display = pdf?.display ?? '#0B2A45'
  const headBg = pdf?.tableHeadBg ?? '#0B2A45'
  const headText = pdf?.tableHeadText ?? '#FFFFFF'
  const banded = (pdf?.tableRules ?? 'ruled') !== 'ruled'
  const zebra = Boolean(pdf?.zebra)
  const zebraBg = pdf?.zebraBg ?? '#F4F7FA'
  const hairline = pdf?.hairline ?? '#DDE4EA'

  const subject = useMemo(
    () =>
      subjectFor(
        spec,
        filters,
        deals.find((candidate) => candidate.id === filters.loan_id)?.property ?? null,
      ),
    [deals, filters, spec],
  )
  const sections = useMemo(() => sectionsFor(spec, filters), [filters, spec])

  return (
    <div className="p-3">
      <div className="mx-auto max-w-[520px] bg-white text-[#1B1F24] shadow-lg" data-testid="live-preview-document">
        {/* Cover */}
        <div className="px-6 pb-5 pt-6" style={{ borderBottom: `2px solid ${accent}` }}>
          <div
            className="text-[8px] font-semibold uppercase tracking-[0.18em]"
            style={{ color: accent }}
          >
            {spec.name}
          </div>
          <h2 className="mt-2 text-[22px] font-bold leading-tight" style={{ color: display }}>
            {name.trim() || subject}
          </h2>
          <div className="mt-1 text-[10px] text-[#3A424B]">{subject}</div>
          <div className="mt-4 grid grid-cols-3 gap-px" style={{ backgroundColor: hairline }}>
            {[
              ['Records', String(scopeRows.length)],
              ['Sections', String(sections.length)],
              ['Format', (spec.formats[0] ?? 'pdf').toUpperCase()],
            ].map(([label, value]) => (
              <div key={label} className="px-2 py-2" style={{ backgroundColor: pdf?.accentPale ?? '#E8F4FB' }}>
                <div className="text-[12px] font-bold" style={{ color: display }}>
                  {value}
                </div>
                <div className="text-[6.5px] font-semibold uppercase tracking-[0.12em] text-[#6E7781]">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contents */}
        <div className="px-6 py-5">
          <div className="text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: display }}>
            Contents
          </div>
          <div className="mt-2 space-y-1">
            {sections.map((section, index) => (
              <div key={section} className="flex items-baseline gap-2 text-[9px]">
                <span className="font-bold" style={{ color: accent }}>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="flex-1 truncate text-[#1B1F24]">{section}</span>
                <span className="border-b border-dotted border-[#98A2AC] flex-1" />
              </div>
            ))}
          </div>
        </div>

        {/* Scope */}
        <div className="px-6 pb-6">
          <div className="text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: display }}>
            {scopeLabel}
          </div>
          {scopeRows.length === 0 ? (
            <p className="mt-2 text-[9px] italic text-[#6E7781]">{scopeEmptyMessage}</p>
          ) : (
            <table className="mt-2 w-full border-collapse text-[8.5px]">
              <thead>
                <tr
                  style={
                    banded
                      ? { backgroundColor: headBg, color: headText }
                      : { borderBottom: `1.5px solid ${display}`, color: '#6E7781' }
                  }
                >
                  <th className="px-1.5 py-1 text-left font-semibold uppercase tracking-wide">Record</th>
                  <th className="px-1.5 py-1 text-left font-semibold uppercase tracking-wide">Detail</th>
                  <th className="px-1.5 py-1 text-right font-semibold uppercase tracking-wide">Value</th>
                </tr>
              </thead>
              <tbody>
                {scopeRows.slice(0, 12).map((row, index) => (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: `0.5px solid ${hairline}`,
                      backgroundColor: zebra && index % 2 === 1 ? zebraBg : undefined,
                    }}
                  >
                    <td className="px-1.5 py-1">{row.primary}</td>
                    <td className="px-1.5 py-1 text-[#3A424B]">{row.secondary}</td>
                    <td className="px-1.5 py-1 text-right tabular-nums">{row.trailing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {scopeRows.length > 12 && (
            <p className="mt-1 text-[8px] text-[#6E7781]">
              …and {scopeRows.length - 12} more in the produced document.
            </p>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-6 py-2 text-[7px]"
          style={{ backgroundColor: pdf?.displayDeep ?? '#071B2E', color: '#FFFFFF' }}
        >
          <span>{pdf?.confidentialityLine || 'Private and confidential'}</span>
          <span>{pdf?.footerCentre || ''}</span>
        </div>
      </div>
    </div>
  )
}

/** Turn a failed preview into something the desk can act on. */
async function previewErrorMessage(cause: unknown): Promise<string> {
  const response = (cause as { response?: { data?: unknown; status?: number } })?.response
  const data = response?.data
  // The preview asks for a blob, so a JSON error arrives as one and has to be
  // read back before its detail can be shown.
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { detail?: string }
      if (parsed?.detail) return parsed.detail
    } catch {
      /* fall through to the generic message */
    }
  } else if (data && typeof data === 'object' && 'detail' in data) {
    const detail = (data as { detail?: unknown }).detail
    if (typeof detail === 'string') return detail
  }
  if (cause instanceof Error && cause.message) return cause.message
  return 'The preview could not be rendered.'
}
